import { useEffect, useRef, useState } from "react";
import { message } from "antd";
import withDashboardLayout from "../../hoc/withDashboardLayout";
import { LeftOutlined, RightOutlined } from "@ant-design/icons";
import { DownloadPayout, GetPayouts } from "../../utils/api";
import MaterialTable from "material-table";
import tableIcons from "../../utils/tableIcons";
// import Loader from "../../components/shared/Loader";
import { downloadPDF } from "../../utils/storageUtil";
import DownloadIcon from "../../assets/download.svg";
import Spinner from "../../components/shared/Spinner";
import dayjs from "dayjs";
import { convertToViewableAmount } from "../../utils/helper";

const Payouts = () => {
  const tableRef = useRef(null);
  const [loading, setLoading] = useState(false);
  const [query, setQuery] = useState({
    page: 0,
    pageSize: 25,
    search: "",
    orderField: "",
    orderDirection: "",
    customFilter: "",
  });

  const downloadPayoutPdf = async (id) => {
    try {
      setLoading((prevState) => ({ ...prevState, [id]: true }));
      const response = await DownloadPayout({ payOutRequestId: id });
      downloadPDF(response);
    } catch (error) {
      message.error(error?.message || "Server Error: Failed to download file.");
    } finally {
      setLoading((prevState) => ({ ...prevState, [id]: false }));
    }
  };

  const columns = [
    {
      title: "ID",
      field: "uniqueId",
      sorting: true,
      cellStyle: { width: "10%" },
    },
    {
      title: "Name",
      field: "name",
      sorting: false,
      cellStyle: { width: "20%" },
      render: (record) => {
        return <span>{record?.user?.name}</span>;
      },
    },
    {
      title: "Date",
      field: "createdAt",
      sorting: false,
      cellStyle: { width: "20%" },
      render: (record) => {
        return (
          <span>{dayjs(record?.createdAt).format("DD/MM/YYYY hh:mm a")}</span>
        );
      },
    },
    {
      title: "Total Amount",
      field: "amount",
      sorting: true,
      cellStyle: { width: "25%" },
      render: (record) => {
        return <span>{convertToViewableAmount(record?.amount)}</span>;
      },
    },

    {
      title: "Status",
      field: "statusType",
      sorting: false,
      cellStyle: { width: "10%" },
      render: (record) => {
        return (
          <span
            className={`capitalize cursor-pointer ${
              record.status === "completed"
                ? "text-[green]"
                : record.status === "pending"
                ? "text-yellow-500"
                : "text-red-900"
            }`}
          >
            {record.status}
          </span>
        );
      },
    },
    {
      sorting: false,
      align: "right",
      render: (record) => {
        return (
          <button
            disabled={loading[record.id]}
            className="text-[12px]"
            onClick={(e) => {
              e.preventDefault();
              downloadPayoutPdf(record.id);
            }}
          >
            {!loading[record.id] ? (
              <img
                src={DownloadIcon}
                width={12}
                alt="download"
                className="flex-shrink-0 mr-4"
              />
            ) : (
              <div className="mr-4">
                <Spinner />
              </div>
            )}
          </button>
        );
      },
    },
  ];

  const fetchData = async (dataquery) => {
    setLoading(true);

    let params = "";

    if (dataquery?.search) {
      params += `search=${dataquery.search}`;
    }

    if (dataquery?.orderBy) {
      params += `&sort=${dataquery?.orderBy.field}&order=${dataquery?.orderDirection}`;
    }

    params += `&pageNo=${dataquery?.page + 1}`;

    params += `&limit=${dataquery?.pageSize}`;

    if (query.customFilter) {
      params += `&statusType=${query.customFilter}`;
    }

    const response = await GetPayouts(params);

    setLoading(false);

    return {
      data: response?.items,
      page: dataquery?.page,
      totalCount: response?.totalRecords,
    };
  };

  const overlayContainer = {
    width: "100%",
    height: "100%",
    backgroundColor: "rgba(255, 255, 255, 0.5)",
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    zIndex: 999,
  };

  useEffect(() => {
    tableRef.current.onQueryChange(query);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [query.customFilter]);

  return (
    <div className="w-full h-full">
      <div className="w-[95%] mx-auto">
        {/* Support Tickets Header */}
        <div className="w-full py-4">
          <h2 className="font-semibold text-lg title text-[#000000]">
            Payouts
          </h2>
        </div>

        {/* Support Tickets */}
        <div className="search-custom-space main-scroll w-full min-h-[calc(100vh-170px)] h-[380px] overflow-scroll flex flex-col gap-y-5 box-shadow rounded-2xl relative">
          <MaterialTable
            title="Payouts"
            tableRef={tableRef}
            columns={columns}
            icons={tableIcons}
            page={query.page}
            options={{
              showTitle: false,
              draggable: false,
              paging: true,
              sorting: true,
              pageSize: query.pageSize,
              pageSizeOptions: [10, 25, 50, 100],
              debounceInterval: 700,
              padding: "dense",
              paginationType: "stepped",
              searchFieldVariant: "outlined",
              searchFieldAlignment: "left",
            }}
            data={(query) => fetchData(query)}
            onChangeRowsPerPage={(pageSize) => {
              setQuery((prev) => ({ ...prev, pageSize: Number(pageSize) }));
            }}
            onChangePage={(page) =>
              setQuery((prev) => ({ ...prev, page: Number(page) }))
            }
            components={{
              Pagination: (props) => {
                const {
                  page,
                  rowsPerPage,
                  count,
                  onChangePage,
                  onChangeRowsPerPage,
                } = props;
                return (
                  <div
                    className="box-shadow-pagination text-sm"
                    style={{
                      position: "relative",
                      display: "flex",
                      justifyContent: "center",
                      alignItems: "center",
                      padding: "10px 20px",
                      marginTop: "20px",
                    }}
                  >
                    <div>
                      <div className="bottom-pagination-text">
                        <button
                          className="disabled:opacity-50"
                          onClick={(e) => onChangePage(e, page - 1)}
                          disabled={page === 0}
                        >
                          <LeftOutlined />
                        </button>
                        <span style={{ margin: "0 10px" }}>
                          {page + 1} of {Math.ceil(count / rowsPerPage)}
                        </span>
                        <button
                          className="disabled:opacity-50"
                          onClick={(e) => onChangePage(e, page + 1)}
                          disabled={
                            page === Math.ceil(count / rowsPerPage) - 1 ||
                            tableRef.current?.dataManager?.data?.length === 0
                          }
                        >
                          <RightOutlined />
                        </button>
                      </div>
                    </div>
                    <div style={{ position: "absolute", right: "25px" }}>
                      <span style={{ margin: "0 10px" }}>Items per page:</span>
                      <select
                        className="pagination-select"
                        disabled={
                          tableRef.current?.dataManager?.data?.length === 0
                        }
                        value={rowsPerPage}
                        onChange={(e) => {
                          onChangeRowsPerPage(e, e.target.value);
                        }}
                      >
                        <option value={10}>10</option>
                        <option value={25}>25</option>
                        <option value={50}>50</option>
                        <option value={100}>100</option>
                      </select>
                    </div>
                  </div>
                );
              },
              OverlayLoading: (props) => {
                return <div style={overlayContainer}>{/* <Loader /> */}</div>;
              },
            }}
          />
        </div>
      </div>
    </div>
  );
};

export default withDashboardLayout(Payouts);
